import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges
} from "@angular/core";
import * as _ from "lodash";
import {uniq} from "lodash";
import {PlanningViewSettingsRouteModel} from "../../../models/planning-view-settings.model";
import {AddressRequestType} from "../../../../addresses/angular/types/address-request.type";
import {LocationType} from "../../../../commons/types/location.type";
import {PanelService} from "../../../../commons/angular/services/panel.service";

require('./planning-draft-route-panel-activity-item.scss');
@Component({
  standalone: false,
  selector: 'app-planning-draft-route-panel-activity-item',
  templateUrl: './planning-draft-route-panel-activity-item.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PlanningDraftRoutePanelActivityItemComponent implements OnInit, OnChanges {

  @Input() activity: any;
  @Input() isActivityLocked: Boolean;
  @Input() index: number;
  @Input() job?: any;
  @Input() jobs?: any;
  @Input() route?: any;
  @Input() settings: PlanningViewSettingsRouteModel;
  @Input() highlightedActivity?: any;
  @Input() itemElementSize: string;
  @Input() isSmallScreen: boolean;
  @Input() isSelected: boolean;
  @Input() isDisplayRetrips: boolean;
  @Input() queryText: string;
  @Output() onViewJob = new EventEmitter<{ jobs: any[], event?: any }>();
  @Output() onToggle = new EventEmitter<boolean>();
  @Output() onToggleObligateTimeWindow = new EventEmitter<{ jobIds: string[], obligateTimeWindow: boolean }>();
  @Output() onToggleLockJobs = new EventEmitter<{ jobIds: string[], isLocked: boolean }>();
  @Output() onContextMenu = new EventEmitter<{ job: any, event: MouseEvent }>();


  public barcodesList: string[];
  public labels: string[];
  public packages: string[];
  public routeColor: string;
  public dateRange: { notBefore: string, notAfter: string };
  public isHover: boolean;
  public stopRef?: any;
  public query: any;
  public type: AddressRequestType;
  public showAddressIcon: boolean;
  public isAccountHover: boolean;
  public jobIds: string[];
  public isObligatedTimeWindow: boolean = false;
  public lastSyncedFromStepAt: any;
  public isShowObligateTimeWindowToggle: boolean = false;
  public isHighlightObligateTimeWindowToggle: boolean = false;
  public accountsName: string[] = [];
  public isRunning: boolean = false;


  constructor(@Inject('planningCommonService') private planningCommonService,
              @Inject('$timeout') private $timeout,
              @Inject(PanelService) private panelService: PanelService
              ) {
  }

  ngOnInit() {

    const jobs = this.job ? [this.job] : (this.jobs || []);

    let packages = [];
    let barcodesList = [];
    let labels = [];

    jobs.forEach((j) => {
      let jobPackages = [];
      let jobBarcodesList = j.packages?.filter((p) => !!p.barcode).map((p) => p.barcode);
      let jobLabels = [];

      j.packages?.forEach(p => {
        jobPackages = jobPackages.concat(p);


        if (p.labels?.length) {
          jobLabels = jobLabels.concat(p.labels);
        }
      })

      barcodesList = barcodesList.concat(jobBarcodesList);
      labels = labels.concat(jobLabels);
      packages = packages.concat(jobPackages);
    })

    this.packages = _.uniqBy(packages, 'size');
    this.barcodesList = _.uniq(barcodesList);
    this.labels = _.uniq(labels);

    if (this.activity?.type === 'GROUP' && this.activity?.stopActivities?.length > 0) {
      this.accountsName = uniq(this.activity.stopActivities?.map(a => a.accountName));
    } else {
      this.accountsName.push(this.activity?.accountName || '---');
    }

    if (this.job) {
      this.stopRef = _.find(this.job.stops, (s: any) => {
        return !!this.activity.stopIds?.length ? _.includes(this.activity.stopIds, s.id) : s.id === this.activity.stopId
      })

      this.query = {
        api: '/planning/projects/{projectId}/drafts/{draftId}/address/{addressId}',
        draftId: this.job.draftId,
        projectId: this.job.projectId
      }
      this.type = AddressRequestType.Planning;
    }

    // For GROUP activities, search through all jobs
    if (!this.stopRef && this.jobs?.length) {
      const allStops = _.flatMap(this.jobs, 'stops');
      this.stopRef = _.find(allStops, (s: any) => {
        return !!this.activity.stopIds?.length ? _.includes(this.activity.stopIds, s.id) : s.id === this.activity.stopId
      });
    }


    this._setObligateTimeWindow();
    this._setLastSyncedFromStepAt();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.activity) {
      this.jobIds = this.activity?.jobIds?.length ? this.activity.jobIds : _.compact([this.activity?.jobId]);
    }
    if (changes.jobs && !changes.jobs.isFirstChange()) {
      this._setObligateTimeWindow();
      this._setLastSyncedFromStepAt();
    }
    if (changes.route) {
      this.setRouteColor();
      this._setIsRouteProcessRunning();
    }
  }

  isValidAddressType(address) {
    return address.locationType == LocationType.ACCOUNT ||
      address.locationType == LocationType.DEPOT ||
      address.locationType == LocationType.RESOURCE ||
      address.locationType == LocationType.CUSTOMER;
  }


  onHover(isHover) {
    this.isShowObligateTimeWindowToggle = isHover;
    if (this.route.locked) return;
    this.isHover = isHover;
    this.$timeout(0).then(() => {
    });
  }

  isHighQualityAddress(address) {
    return address && address.addressResolvingInfo &&
      address.addressResolvingInfo.qualityGroup == "High";
  }

  onClick(event: MouseEvent) {
    if (event.ctrlKey) {
      this.onToggle.emit(!this.isSelected)
      this.$timeout(0).then(() => {
      });
    }
  }

  private setRouteColor() {
    this.routeColor = this.planningCommonService.getRouteColor(this.route);
  }

  private _setIsRouteProcessRunning() {
    this.isRunning = this.route?.actionProcess?.running && (new Date().getTime() < this.route?.actionProcess?.expirationDate);
  }

  private _setObligateTimeWindow() {
    this.isObligatedTimeWindow = false;
    const stops = _.flatMap(this.jobs, 'stops');

    if (!stops?.length) {
      return;
    }
    this.isObligatedTimeWindow = stops.every((stop) => !!stop.obligateTimeWindow);
  }

  private _setLastSyncedFromStepAt() {
    const jobRef = _.find(this.jobs, 'lastSyncedFromStepAt');
    this.lastSyncedFromStepAt = jobRef?.lastSyncedFromStepAt;
  }

  get displayAccountName(): string {
    // For GROUP activities, get account name from first stopActivity
    if (this.activity?.type === 'GROUP' && this.activity?.stopActivities?.length > 0) {
      return this.activity.stopActivities[0]?.accountName || '---';
    }
    // For regular activities, use the activity's accountName
    return this.activity?.accountName || '---';
  }
}
